//Core imports
import { Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import { BaseRoutes } from "./packages/utils/routes";

//Functional & Workflow imports
import ProtectedRoutes from "./ProtectedRoutes";
import {
  AddTenant,
  AdvisoryService,
  AmcConfigurationService,
  BITool,
  BankAccountVerificationsListing,
  BankSettings,
  BatchFilesListing,
  BlockedBankAccounts,
  CreateSupportIssue,
  EditNotificationWebhooks,
  EnableBank,
  FeedSubmissionJobsListing,
  FeedSubmissionsListing,
  FolioDetails,
  FolioListing,
  FpAssist,
  FundSchemesListing,
  HealthMetrics,
  IFSCCodes,
  InternalUsersManagement,
  InvestmentAccountsDetails,
  InvestmentAccountsListing,
  InvestorDetails,
  InvestorProfileDetails,
  InvestorProfilesListing,
  InvestorsListing,
  KYCRequestsDetails,
  KYCRequestsListing,
  KycFormDetails,
  KycFormsListing,
  KYCService,
  ListAgingOrders,
  ListApiLogs,
  ListFiles,
  ListPaymentTransfers,
  ManageConsolidatedActions,
  ManageOrderFailureCodes,
  ManageOrderOperations,
  ManagePaymentsGateway,
  ManageUser,
  MandatesDetails,
  MandatesListing,
  ManualBankAccountVerificationListing,
  MonitoringAlertsListing,
  NonIndividualInvestorProfileDetails,
  NonIndividualInvestorProfilesListing,
  NotFoundPage,
  NotificationWebhooks,
  OrderChartsListing,
  OrderConfigurationService,
  OrderInsights,
  PartnerAddition,
  PartnerDetails,
  PartnersListing,
  UpcomingDistributorsListing,
  PaymentDetails,
  PaymentListing,
  OrderFeedsListing,
  OrderFeedDetails,
  PayoutDetails,
  PayoutsListing,
  ReconDetails,
  ReconListing,
  DepositReceiptsListing,
  DepositReceiptAdjustmentsListing,
  DepositReceiptAdjustmentDetails,
  ReconAdjustmentsListing,
  ReconAdjustmentDetails,
  DepositReceiptDetails,
  FeedConfiguration,
  SettlementInstructionsListing,
  SettlementInstructionDetails,
  SettlementReportFilesListing,
  BankAccountStatementsListing,
  BankAccountStatementDetails,
  Pincodes,
  ProductUsage,
  PurchaseDetails,
  PurchaseListing,
  PurchasePlanDetails,
  PurchasePlanListing,
  RedemptionDetails,
  RedemptionListing,
  RedemptionPlanDetails,
  RedemptionPlanListing,
  RegenerateInvestorFiles,
  RequireSupportAccess,
  ScheduledQueries,
  ScheduledQueryRuns,
  SchemeDetails,
  SchemeManagement,
  ShowTransferDetails,
  SupportIssueDetails,
  SupportIssues,
  SwitchDetails,
  SwitchListing,
  SwitchPlanDetails,
  SwitchPlanListing,
  TenantUsersManagement,
  TenantsListing,
  TransactionsListing,
  UpdateBankSettings,
  OndcTransactionsListing,
  OndcTransactionsDetails,
  RequestFeedFile,
  VerificationService,
  VerifyBankAccount,
  ViewApiLogDetaills,
  KycInsights,
  ApiMetrics,
  PanVerificationsListing,
  KycStatusChecksListing,
  BankAccountVerificationsStandaloneListing,
} from "./packages";

//Display
import RequireEnv from "./packages/accessControl/RequireEnv";
import Landing from "./packages/components/display/Landing";
import { EventsListing } from "./packages/components/functional/Events";
import FileOperationsListing from "./packages/components/functional/FileOperations/FileOperationsListing";
import ConfigurationStatus from "./packages/components/functional/FpAccount/ConfigurationStatus/ConfigurationStatus";
import PurchasesOverview from "./packages/components/functional/Orders/PurchaseOverview/PurchaseOverview";
import { OndcRoutesWrapper } from "./packages/context/ondc";
import { Role } from "./packages/context/userinfo/types";
import { useUserInfo } from "./packages/context/userinfo/useUserInfo";
import { useGlobalAppStore } from "./packages/context/store/useGlobalAppStore";
import { PURCHASE_OVERVIEW_ENABLED_TENANTS } from "./packages/utils/constants";
import { tenantEnv, isOnlyOndcAnalyst } from "./packages/utils/utils";
import {
  LegacyInvestorOverviewRedirect,
  LegacyInvestorProfileRedirect,
  LegacyInvestorV1Redirect,
} from "./packages/components/functional/Investors/LegacyInvestorRedirects";
import { isInvestorsV1Accessible } from "./packages/components/functional/Investors/utils";
import { isKycStatusChecksAccessible } from "./packages/components/functional/Verifications/KycStatusChecks/utils";

export default function AppWithRoutes() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { isAmcTenant, supportedGateways } = useGlobalAppStore();
  const canAccessInvestorsV1 = isInvestorsV1Accessible(supportedGateways, isAmcTenant);
  const canAccessKycStatusChecks = isKycStatusChecksAccessible(
    userInfo?.data?.activeTenant,
    isAmcTenant
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Landing
            onRender={() => {
              if (
                userInfo?.data?.userInfo?.roles?.length === 1 &&
                userInfo?.data?.userInfo?.roles?.[0] === Role.TENANT_BUSINESS_ANALYST
              ) {
                navigate(
                  `${BaseRoutes.ORDERS}/orders-trend?order_type=all_purchases%2Call_redemptions&fund_category=all&chart_metric_type=count`
                );
                return;
              }

              if (
                tenantEnv.isStagingOrSandbox &&
                PURCHASE_OVERVIEW_ENABLED_TENANTS?.includes(userInfo?.data?.activeTenant ?? "")
              ) {
                navigate(`${BaseRoutes.ORDERS}/overview`);
              }
            }}
          />
        }
      />

      {/*************************** FP Console Routes ***************************/}

      <Route
        path="/fp-console/order-failure-code-list"
        element={
          <ProtectedRoutes
            path="/fp-console/order-failure-code-list"
            component={<ManageOrderFailureCodes />}
          />
        }
      />

      <Route
        path="/fp-console/order-failure-code"
        element={
          <ProtectedRoutes
            path="/fp-console/order-failure-code"
            component={<ManageOrderFailureCodes />}
          />
        }
      />

      <Route
        path="/fp-console/action-center"
        element={
          <ProtectedRoutes
            path="/fp-console/action-center"
            component={<ManageConsolidatedActions />}
          />
        }
      />

      <Route
        path="/fp-console"
        element={
          <ProtectedRoutes
            path="/fp-console"
            component={
              <Landing
                onRender={() => {
                  if (isOnlyOndcAnalyst(userInfo?.data?.userInfo?.roles)) {
                    navigate("/fp-console/order-insights");
                  }
                }}
              />
            }
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/tenants"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/tenants"
            component={<TenantsListing />}
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/tenants/new"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/tenants/new"
            component={<AddTenant />}
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/user-management"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/user-management"
            component={<InternalUsersManagement />}
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/user-management/new"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/user-management/new"
            component={
              <ManageUser
                mode="CREATE"
                userCreationType="CYBRILLA_INTERNAL"
                onBack={() => {
                  navigate("/fp-console/fp-account-details/user-management");
                }}
                onComplete={() => {
                  navigate("/fp-console/fp-account-details/user-management");
                }}
              />
            }
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/user-management/root/new"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/user-management/root/new"
            component={
              <ManageUser
                mode="CREATE"
                userCreationType="TENANT_ROOT"
                onBack={() => {
                  navigate("/fp-console/fp-account-details/user-management");
                }}
                onComplete={() => {
                  navigate("/fp-console/fp-account-details/user-management");
                }}
              />
            }
          />
        }
      />

      <Route
        path="/fp-console/fp-account-details/user-management/:id"
        element={
          <ProtectedRoutes
            path="/fp-console/fp-account-details/user-management/:id"
            component={
              <ManageUser
                mode="EDIT"
                userCreationType="CYBRILLA_INTERNAL"
                onBack={() => {
                  navigate("/fp-console/fp-account-details/user-management");
                }}
                onComplete={({ data }) => {
                  navigate("/fp-console/fp-account-details/user-management");

                  if (data?.username === userInfo?.data.userInfo?.email) {
                    window.location.reload();
                  }
                }}
              />
            }
          />
        }
      />

      <Route
        path="/fp-console/pincodes"
        element={<ProtectedRoutes path="/fp-console/pincodes" component={<Pincodes />} />}
      />

      <Route
        path="/fp-console/ifsc-codes"
        element={<ProtectedRoutes path="/fp-console/ifsc-codes" component={<IFSCCodes />} />}
      />

      <Route
        path="/fp-console/scheme-master-management"
        element={
          <ProtectedRoutes
            path="/fp-console/scheme-master-management"
            component={<SchemeManagement />}
          />
        }
      />

      <Route
        path={`/fp-console/order-insights`}
        element={
          <ProtectedRoutes path={`/fp-console/order-insights`} component={<OrderInsights />} />
        }
      />

      <Route
        path={`/fp-console/kyc-insights`}
        element={<ProtectedRoutes path={`/fp-console/kyc-insights`} component={<KycInsights />} />}
      />

      <Route
        path={`/fp-console/health-metrics`}
        element={
          <ProtectedRoutes path={`/fp-console/health-metrics`} component={<HealthMetrics />} />
        }
      />

      <Route
        path={`/fp-console/api-metrics`}
        element={
          <ProtectedRoutes
            path={`/fp-console/api-metrics`}
            component={
              <RequireEnv envCondition={tenantEnv.isStagingOrSandbox}>
                <ApiMetrics />
              </RequireEnv>
            }
          />
        }
      />

      {/*************************** Operations routes ***************************/}

      <Route
        path={`${BaseRoutes.OPERATIONS}/order-operations`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/order-operations`}
            component={<ManageOrderOperations />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/order-operations/files/regenerate`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/order-operations/files/regenerate`}
            component={
              <RegenerateInvestorFiles
                orderId={searchParams.get("order_id")}
                mfiaId={searchParams.get("mfia_id")}
              />
            }
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/order-operations/files`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/order-operations/files`}
            component={<ListFiles />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/order-operations/batch-files`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/order-operations/batch-files`}
            component={<BatchFilesListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/feed-file`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/feed-file`}
            component={<RequestFeedFile />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/ondc-transactions`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/ondc-transactions`}
            component={<OndcTransactionsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/ondc-transactions/:transaction_id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/ondc-transactions/:transaction_id`}
            component={<OndcTransactionsDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/aging-orders`}
        element={
          <ProtectedRoutes
            component={<ListAgingOrders />}
            path={`${BaseRoutes.OPERATIONS}/aging-orders`}
          />
        }
      />

      <Route
        path={BaseRoutes.TRANSACTIONS}
        element={
          <ProtectedRoutes path={BaseRoutes.TRANSACTIONS} component={<TransactionsListing />} />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/transfers`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/transfers`}
            component={<ListPaymentTransfers />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/transfers/:transfer_reference`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/transfers/:transfer_reference`}
            component={<ShowTransferDetails paymentTransfers={[]} currentTransferId={null} />}
          />
        }
      />

      <Route
        path={BaseRoutes.MF_FOLIOS}
        element={<ProtectedRoutes path={BaseRoutes.MF_FOLIOS} component={<FolioListing />} />}
      />
      <Route
        path={`${BaseRoutes.MF_FOLIOS}/:folio_number`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MF_FOLIOS}/:folio_number`}
            component={<FolioDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/fund-schemes`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/fund-schemes`}
            component={<FundSchemesListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/fund-schemes/scheme-details/:scheme_isin`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/fund-schemes/scheme-details/:scheme_isin`}
            component={<SchemeDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/order-operations/file-operations`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/order-operations/file-operations`}
            component={<FileOperationsListing showBackButton={true} />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/blocked-bank-accounts`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/blocked-bank-accounts`}
            component={<BlockedBankAccounts />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/manual-bank-account-verification`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/manual-bank-account-verification`}
            component={<ManualBankAccountVerificationListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.OPERATIONS}/manual-bank-account-verification/verify/:id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.OPERATIONS}/manual-bank-account-verification/verify/:id`}
            component={<VerifyBankAccount />}
          />
        }
      />
      {/*************************** Order Routes ***************************/}
      <Route
        path={`${BaseRoutes.ORDERS}/overview`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.ORDERS}/overview`}
            component={<PurchasesOverview />}
          />
        }
      />

      <Route element={<OndcRoutesWrapper />}>
        <Route
          path={BaseRoutes.MF_PURCHASES}
          element={
            <ProtectedRoutes path={BaseRoutes.MF_PURCHASES} component={<PurchaseListing />} />
          }
        />

        <Route
          path={`${BaseRoutes.MF_PURCHASES}/:purchaseId`}
          element={
            <ProtectedRoutes
              path={`${BaseRoutes.MF_PURCHASES}/:purchaseId`}
              component={<PurchaseDetails />}
            />
          }
        />

        <Route
          path={BaseRoutes.MF_REDEMPTIONS}
          element={
            <ProtectedRoutes path={BaseRoutes.MF_REDEMPTIONS} component={<RedemptionListing />} />
          }
        />

        <Route
          path={`${BaseRoutes.MF_REDEMPTIONS}/:redemptionId`}
          element={
            <ProtectedRoutes
              path={`${BaseRoutes.MF_REDEMPTIONS}/:redemptionId`}
              component={<RedemptionDetails />}
            />
          }
        />

        <Route
          path={BaseRoutes.MF_SWITCHES}
          element={<ProtectedRoutes path={BaseRoutes.MF_SWITCHES} component={<SwitchListing />} />}
        />

        <Route
          path={`${BaseRoutes.MF_SWITCHES}/:switchId`}
          element={
            <ProtectedRoutes
              path={`${BaseRoutes.MF_SWITCHES}/:switchId`}
              component={<SwitchDetails />}
            />
          }
        />
      </Route>

      <Route
        path={BaseRoutes.MF_PURCHASE_PLANS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.MF_PURCHASE_PLANS}
            component={<PurchasePlanListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.MF_PURCHASE_PLANS}/:purchasePlanId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MF_PURCHASE_PLANS}/:purchasePlanId`}
            component={<PurchasePlanDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.MF_REDEMPTION_PLANS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.MF_REDEMPTION_PLANS}
            component={<RedemptionPlanListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.MF_REDEMPTION_PLANS}/:redemptionPlanId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MF_REDEMPTION_PLANS}/:redemptionPlanId`}
            component={<RedemptionPlanDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.MF_SWITCH_PLANS}
        element={
          <ProtectedRoutes path={BaseRoutes.MF_SWITCH_PLANS} component={<SwitchPlanListing />} />
        }
      />

      <Route
        path={`${BaseRoutes.MF_SWITCH_PLANS}/:switchPlanId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MF_SWITCH_PLANS}/:switchPlanId`}
            component={<SwitchPlanDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.ORDERS}/orders-trend`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.ORDERS}/orders-trend`}
            component={<OrderChartsListing />}
          />
        }
      />

      {/*************************** Metrics Routes ***************************/}
      <Route
        path={`${BaseRoutes.METRICS}/product-usage`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.METRICS}/product-usage`}
            component={<ProductUsage />}
          />
        }
      />

      {/*************************** Payment Routes ***************************/}
      <Route
        path={BaseRoutes.PAYMENTS}
        element={<ProtectedRoutes path={BaseRoutes.PAYMENTS} component={<PaymentListing />} />}
      />

      <Route
        path={BaseRoutes.PAYOUTS}
        element={<ProtectedRoutes path={BaseRoutes.PAYOUTS} component={<PayoutsListing />} />}
      />

      <Route
        path={`${BaseRoutes.ORDERFEED}/:orderFeedId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.ORDERFEED}/:orderFeedId`}
            component={<OrderFeedDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.ORDERFEED}
        element={<ProtectedRoutes path={BaseRoutes.ORDERFEED} component={<OrderFeedsListing />} />}
      />

      <Route
        path={`${BaseRoutes.RECON}/:reconId`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.RECON}/:reconId`} component={<ReconDetails />} />
        }
      />

      <Route
        path={BaseRoutes.RECON}
        element={<ProtectedRoutes path={BaseRoutes.RECON} component={<ReconListing />} />}
      />

      <Route
        path={BaseRoutes.FEED_SUBMISSION_JOBS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.FEED_SUBMISSION_JOBS}
            component={<FeedSubmissionJobsListing />}
          />
        }
      />

      <Route
        path={BaseRoutes.FEED_SUBMISSIONS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.FEED_SUBMISSIONS}
            component={<FeedSubmissionsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.DEPOSIT_RECEIPTS}/:depositReceiptId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.DEPOSIT_RECEIPTS}/:depositReceiptId`}
            component={<DepositReceiptDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.DEPOSIT_RECEIPTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.DEPOSIT_RECEIPTS}
            component={<DepositReceiptsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS}/:depositReceiptAdjustmentId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS}/:depositReceiptAdjustmentId`}
            component={<DepositReceiptAdjustmentDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.DEPOSIT_RECEIPT_ADJUSTMENTS}
            component={<DepositReceiptAdjustmentsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.RECON_ADJUSTMENTS}/:reconAdjustmentId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.RECON_ADJUSTMENTS}/:reconAdjustmentId`}
            component={<ReconAdjustmentDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.RECON_ADJUSTMENTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.RECON_ADJUSTMENTS}
            component={<ReconAdjustmentsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.SETTLEMENT_INSTRUCTIONS}/:settlementInstructionId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.SETTLEMENT_INSTRUCTIONS}/:settlementInstructionId`}
            component={<SettlementInstructionDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.SETTLEMENT_INSTRUCTIONS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.SETTLEMENT_INSTRUCTIONS}
            component={<SettlementInstructionsListing />}
          />
        }
      />

      <Route
        path={BaseRoutes.SETTLEMENT_REPORT_FILES}
        element={
          <ProtectedRoutes
            path={BaseRoutes.SETTLEMENT_REPORT_FILES}
            component={<SettlementReportFilesListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.BANK_ACCOUNT_STATEMENTS}/:bankAccountStatementId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.BANK_ACCOUNT_STATEMENTS}/:bankAccountStatementId`}
            component={<BankAccountStatementDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.BANK_ACCOUNT_STATEMENTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.BANK_ACCOUNT_STATEMENTS}
            component={<BankAccountStatementsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.PAYOUTS}/:payoutId`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.PAYOUTS}/:payoutId`} component={<PayoutDetails />} />
        }
      />

      <Route
        path={`${BaseRoutes.PAYMENTS}/:paymentId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.PAYMENTS}/:paymentId`}
            component={<PaymentDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.MANDATES}
        element={<ProtectedRoutes path={BaseRoutes.MANDATES} component={<MandatesListing />} />}
      />

      <Route
        path={`${BaseRoutes.MANDATES}/:mandateId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MANDATES}/:mandateId`}
            component={<MandatesDetails />}
          />
        }
      />

      {/*************************** Investor Routes ***************************/}

      <Route
        path={BaseRoutes.MF_INVESTMENT_ACCOUNTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.MF_INVESTMENT_ACCOUNTS}
            component={<InvestmentAccountsListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.MF_INVESTMENT_ACCOUNTS}/:mfiaId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.MF_INVESTMENT_ACCOUNTS}/:mfiaId`}
            component={<InvestmentAccountsDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.INVESTORS}/investors-overview`}
        element={<LegacyInvestorOverviewRedirect />}
      />

      <Route
        path={`${BaseRoutes.INVESTORS}/investors-overview/v1/:investorId`}
        element={<LegacyInvestorV1Redirect />}
      />

      <Route
        path={`${BaseRoutes.INVESTORS}/investors-overview/v2/:investorProfileId`}
        element={<LegacyInvestorProfileRedirect />}
      />

      <Route
        path={BaseRoutes.INVESTORS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.INVESTORS}
            component={<InvestorsListing />}
            condition={canAccessInvestorsV1}
          />
        }
      />

      <Route
        path={`${BaseRoutes.INVESTORS}/:investorId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.INVESTORS}/:investorId`}
            component={<InvestorDetails />}
            condition={canAccessInvestorsV1}
          />
        }
      />

      <Route
        path={BaseRoutes.INVESTOR_PROFILES}
        element={
          <ProtectedRoutes
            path={BaseRoutes.INVESTOR_PROFILES}
            component={<InvestorProfilesListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.INVESTOR_PROFILES}/:investorProfileId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.INVESTOR_PROFILES}/:investorProfileId`}
            component={<InvestorProfileDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.BANK_ACCOUNT_VERIFICATIONS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.BANK_ACCOUNT_VERIFICATIONS}
            component={<BankAccountVerificationsListing />}
          />
        }
      />

      <Route
        path={BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS}
            component={<NonIndividualInvestorProfilesListing />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS}/:nonIndividualInvestorProfileId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.INVESTOR_PROFILE_NON_INDIVIDUALS}/:nonIndividualInvestorProfileId`}
            component={<NonIndividualInvestorProfileDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.KYC_REQUESTS}
        element={
          <ProtectedRoutes path={BaseRoutes.KYC_REQUESTS} component={<KYCRequestsListing />} />
        }
      />

      <Route
        path={BaseRoutes.KYC_FORMS}
        element={<ProtectedRoutes path={BaseRoutes.KYC_FORMS} component={<KycFormsListing />} />}
      />

      <Route
        path={`${BaseRoutes.KYC_FORMS}/:kycFormId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.KYC_FORMS}/:kycFormId`}
            component={<KycFormDetails />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.KYC_REQUESTS}/:kycRequestId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.KYC_REQUESTS}/:kycRequestId`}
            component={<KYCRequestsDetails />}
          />
        }
      />

      {/*************************** Developer Routes ***************************/}

      <Route
        path={`${BaseRoutes.DEVELOPERS}/api-logs`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.DEVELOPERS}/api-logs`} component={<ListApiLogs />} />
        }
      />

      <Route
        path={BaseRoutes.MONITORING_ALERTS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.MONITORING_ALERTS}
            component={<MonitoringAlertsListing />}
          />
        }
      />

      <Route
        path={BaseRoutes.EVENTS}
        element={<ProtectedRoutes path={BaseRoutes.EVENTS} component={<EventsListing />} />}
      />

      <Route
        path={`${BaseRoutes.DEVELOPERS}/api-logs/:id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.DEVELOPERS}/api-logs/:id`}
            component={<ViewApiLogDetaills />}
          />
        }
      />

      {/*************************** FP Account Routes ***************************/}

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/verifications`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/verifications`}
            component={<VerificationService />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/payments_gateway`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/payments_gateway`}
            component={<ManagePaymentsGateway />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/mf_services`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/mf_services`}
            component={<OrderConfigurationService />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/amcs`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/amcs`}
            component={<AmcConfigurationService />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/kyc-services`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/kyc-services`}
            component={<KYCService />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/advisory`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/advisory`}
            component={<AdvisoryService />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/bi-tool`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.FP_ACCOUNT}/bi-tool`} component={<BITool />} />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/feed-configuration`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/feed-configuration`}
            component={<FeedConfiguration />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/configuration-status`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/configuration-status`}
            component={<ConfigurationStatus />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/user_management`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/user_management`}
            component={<TenantUsersManagement />}
          />
        }
      />

      <Route
        path={BaseRoutes.SCHEDULED_QUERIES}
        element={
          <ProtectedRoutes path={BaseRoutes.SCHEDULED_QUERIES} component={<ScheduledQueries />} />
        }
      />

      <Route
        path={`${BaseRoutes.SCHEDULED_QUERIES}/:scheduled_query_id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.SCHEDULED_QUERIES}/:scheduled_query_id`}
            component={<ScheduledQueryRuns />}
          />
        }
      />

      <Route
        path={BaseRoutes.NOTIFICATION_WEBHOOKS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.NOTIFICATION_WEBHOOKS}
            component={<NotificationWebhooks />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.NOTIFICATION_WEBHOOKS}/:webhook_id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.NOTIFICATION_WEBHOOKS}/:webhook_id`}
            component={<EditNotificationWebhooks />}
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/user-management/new`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/user-management/new`}
            component={
              <ManageUser
                mode="CREATE"
                userCreationType="TENANT"
                onBack={() => {
                  navigate(`${BaseRoutes.FP_ACCOUNT}/user_management`);
                }}
                onComplete={() => {
                  navigate(`${BaseRoutes.FP_ACCOUNT}/user_management`);
                }}
              />
            }
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/user-management/:id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/user-management/:id`}
            component={
              <ManageUser
                mode="EDIT"
                userCreationType="TENANT"
                onBack={() => {
                  navigate(`${BaseRoutes.FP_ACCOUNT}/user_management`);
                }}
                onComplete={({ data }) => {
                  navigate(`${BaseRoutes.FP_ACCOUNT}/user_management`);

                  if (data?.username === userInfo?.data.userInfo?.email) {
                    window.location.reload();
                  }
                }}
              />
            }
          />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/fp-assist`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.FP_ACCOUNT}/fp-assist`} component={<FpAssist />} />
        }
      />

      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/bank-settings`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/bank-settings`}
            component={<BankSettings />}
          />
        }
      />
      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/bank-settings/new`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/bank-settings/new`}
            component={<EnableBank />}
          />
        }
      />
      <Route
        path={`${BaseRoutes.FP_ACCOUNT}/bank-settings/:id`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.FP_ACCOUNT}/bank-settings/:id`}
            component={<UpdateBankSettings />}
          />
        }
      />

      {/*************************** Verification Routes ***************************/}
      <Route
        path={BaseRoutes.PAN_VERIFICATIONS}
        element={
          <ProtectedRoutes
            path={BaseRoutes.PAN_VERIFICATIONS}
            component={<PanVerificationsListing />}
          />
        }
      />

      <Route
        path={BaseRoutes.KYC}
        element={
          <ProtectedRoutes
            path={BaseRoutes.KYC}
            component={<KycStatusChecksListing />}
            condition={canAccessKycStatusChecks}
          />
        }
      />

      <Route
        path={BaseRoutes.BANK_ACCOUNT_VERIFICATION}
        element={
          <ProtectedRoutes
            path={BaseRoutes.BANK_ACCOUNT_VERIFICATION}
            component={<BankAccountVerificationsStandaloneListing />}
          />
        }
      />

      {/*************************** Partner Routes ***************************/}
      <Route
        path={BaseRoutes.PARTNERS}
        element={<ProtectedRoutes path={BaseRoutes.PARTNERS} component={<PartnersListing />} />}
      />

      <Route
        path={`${BaseRoutes.PARTNERS}/new`}
        element={
          <ProtectedRoutes path={`${BaseRoutes.PARTNERS}/new`} component={<PartnerAddition />} />
        }
      />

      <Route
        path={`${BaseRoutes.PARTNERS}/:partnerId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.PARTNERS}/:partnerId`}
            component={<PartnerDetails />}
          />
        }
      />

      <Route
        path={BaseRoutes.NETWORK_INTELLIGENCE}
        element={
          <ProtectedRoutes
            path={BaseRoutes.NETWORK_INTELLIGENCE}
            component={<UpcomingDistributorsListing />}
          />
        }
      />

      {/*************************** Support ***************************/}
      <Route
        path={`${BaseRoutes.SUPPORT}/issues`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.SUPPORT}/issues`}
            component={
              <RequireEnv envCondition={tenantEnv.isStagingOrSandbox}>
                <RequireSupportAccess>
                  <SupportIssues />
                </RequireSupportAccess>
              </RequireEnv>
            }
          />
        }
      />
      <Route
        path={`${BaseRoutes.SUPPORT}/issues/new`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.SUPPORT}/issues/new`}
            component={
              <RequireEnv envCondition={tenantEnv.isStagingOrSandbox}>
                <RequireSupportAccess>
                  <CreateSupportIssue />
                </RequireSupportAccess>
              </RequireEnv>
            }
          />
        }
      />
      <Route
        path={`${BaseRoutes.SUPPORT}/issues/:issueId`}
        element={
          <ProtectedRoutes
            path={`${BaseRoutes.SUPPORT}/issues/:issueId`}
            component={
              <RequireEnv envCondition={tenantEnv.isStagingOrSandbox}>
                <RequireSupportAccess>
                  <SupportIssueDetails />
                </RequireSupportAccess>
              </RequireEnv>
            }
          />
        }
      />

      {/*************************** Not found route ***************************/}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
